import type { FilterConfigModalItem } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type {
    ModalFiltersConfigSection,
    RefineSectionPresentation,
} from '@rocco/ui/filter-sort/types/filter-config-section';
import {
    getListItemLabel,
    getRangeLabel,
} from '@rocco/ui/filter-sort/components/filters-modal/refine/get-refine-chips';
import { isFilterStateActive } from '@rocco/ui/filter-sort/utils/get-active-filters';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import {
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { FilterStates } from '@rocco/ui/filter-sort/view-models/filter-states';

/**
 * Resolve the live state for a modal filter config item, checking static
 * states first, then the runtime facet options.
 */
export const resolveSectionFilterState = (
    filterConfigItem: FilterConfigModalItem,
    filterStates: FilterStates,
): FilterState | null =>
    filterStates.states[filterConfigItem.filterKey] ??
    filterStates.facet?.facetOptions?.find(
        (facet) => facet.id === filterConfigItem.filterKey,
    )?.state ??
    null;

/**
 * Decide the control shape a section takes in the mobile "refine" sheet.
 *
 * An explicit `mobileRefine` on the section wins. Otherwise: admin-only
 * sections hide behind "More filters", free-text location renders inline,
 * and every other section is a `>` drill-in row — one affordance for the
 * whole filter stack, with the staged summary on the row keeping applied
 * state legible. `pills` and `expander` remain available as explicit
 * per-section overrides.
 */
export const getRefineSectionPresentation = (
    section: ModalFiltersConfigSection,
    filterStates: FilterStates,
): RefineSectionPresentation => {
    if (section.mobileRefine) {
        return section.mobileRefine;
    }

    if (section.adminOnly) {
        return 'moreFilters';
    }

    const hasLocationFilter = section.filters.some((filter) => {
        const state = resolveSectionFilterState(filter, filterStates);
        return state !== null && isLocationFilterState(state);
    });
    if (hasLocationFilter) {
        return 'inline';
    }

    return 'drillIn';
};

/**
 * Staged-value summary for a collapsed row — the selected labels joined
 * with a dot separator, e.g. "In stock · Samples available" or
 * "Price range: $80–$1,200". Empty string when nothing is staged.
 */
export const getSectionSummary = (
    section: ModalFiltersConfigSection,
    filterStates: FilterStates,
): string =>
    section.filters
        .flatMap((filter) => {
            const state = resolveSectionFilterState(filter, filterStates);
            if (!state) return [];

            if (isSingleValueFilterState(state)) {
                return state.checked ? [filter.label ?? state.label] : [];
            }

            if (isRangeFilterState(state)) {
                return isFilterStateActive(state)
                    ? [getRangeLabel(filter.filterKey, state, filter)]
                    : [];
            }

            if (isMultipleValuesFilterState(state)) {
                return state.selectedItems.map((item) =>
                    getListItemLabel(item, filter),
                );
            }

            return [];
        })
        .filter((part): part is string => Boolean(part))
        .join(' · ');
